import { digitsFaToEn } from '@persian-tools/persian-tools';

export interface DoctorHit {
  slug?: string;
  price?: string;
  satisfaction?: number;
  rates_count?: number;
  actions?: { title?: string; top_title?: string; url?: string }[];
  [key: string]: any;
}

export const MIN_REVIEWS = 5;

/** Search order that brings the right pool for each reason (search `order_items`). */
export const ALTERNATIVE_SORT: Record<'faster' | 'other' | 'cheaper', string | undefined> = {
  cheaper: 'clinic_doctor_price',
  faster: 'clinic_first_freeturn',
  other: undefined,
};

export type AlternativeMode = 'faster' | 'other' | 'cheaper';

/** "350,000 تومان" (search card price text) → 350000. */
export const parseTomanText = (text?: string) => {
  const digits = digitsFaToEn(text ?? '').replace(/[^\d]/g, '');
  return digits ? Number(digits) : undefined;
};

// Satisfaction weighted by how many reviews back it: 100% from 3 reviews must not beat 96% from 800.
const quality = (doctor: DoctorHit) => (doctor.satisfaction ?? 0) * Math.log10(10 + (doctor.rates_count ?? 0));
/** First free online-visit turn (search `consult_freeturn`, epoch seconds) in ms. */
const firstTurnMs = (doctor?: DoctorHit) => (Number(doctor?.consult_freeturn) > 0 ? Number(doctor?.consult_freeturn) * 1000 : undefined);
// "Faster" must be clearly faster, not the same slot ten minutes apart.
const FASTER_BY_MS = 30 * 60_000;

/** Online-visit doctors of the same specialty to offer instead of the one the patient is leaving. */
export const pickAlternativeDoctors = (
  hits: DoctorHit[],
  {
    mode,
    currentSlug,
    currentPriceToman,
    currentTurnAt,
    limit = 2,
  }: { mode: AlternativeMode; currentSlug: string; currentPriceToman?: number; /** Held turn time (ms). */ currentTurnAt?: number; limit?: number },
) => {
  const current = hits.find(doctor => doctor.slug === currentSlug);
  const ceiling = parseTomanText(current?.price) ?? currentPriceToman;
  // Only doctors whose satisfaction is backed by enough reviews to be shown on their profile.
  let list = hits.filter(doctor => doctor.slug && doctor.slug !== currentSlug && (doctor.rates_count ?? 0) >= MIN_REVIEWS);
  if (mode === 'cheaper') {
    if (!ceiling) return [];
    list = list.filter(doctor => (parseTomanText(doctor.price) ?? Infinity) < ceiling);
  }
  if (mode === 'faster') {
    const heldAt = currentTurnAt ?? firstTurnMs(current);
    if (!heldAt) return [];
    list = list.filter(doctor => (firstTurnMs(doctor) ?? Infinity) <= heldAt - FASTER_BY_MS);
    return [...list].sort((a, b) => firstTurnMs(a)! - firstTurnMs(b)! || quality(b) - quality(a)).slice(0, limit);
  }
  return [...list].sort((a, b) => quality(b) - quality(a)).slice(0, limit);
};
